<template>
  <content-dialog
    ref="dialog"
    v-model="dialogOpen"
    no-backdrop-dismiss
    fullscreen-tablet
    height="85vh"
    :width="`${1200 + (filterDrawerOpen ? filterDrawerWidth : 0)}px`"
    :persistent="uploading"
    :loading="uploading || loading"
  >
    <template #card>
      <q-layout
        container
        view="hHr lpR fFr"
      >
        <q-header
          v-if="$refs.dialog"
          class="blurred-container text-black"
        >
          <q-toolbar>
            <q-btn
              v-close-popup
              dense
              flat
              round
              icon="close"
              :loading="uploading"
            />
            <page-toolbar-title>
              <span>{{ dialogTitleStr }}</span>
              <span
                v-if="maximum > 1"
                :class="{'text-red-7': hasReachedMaximum}"
              >
                ({{ tempSelected.length }}/{{ maximum }})
              </span>
            </page-toolbar-title>
            <q-btn
              flat
              round
              dense
              icon="mdi-filter-variant"
              class="q-mr-sm"
              @click="filterDrawerOpen = !filterDrawerOpen"
            >
              <q-tooltip>Toggle Filters</q-tooltip>
            </q-btn>
            <help-button filter="files" />
          </q-toolbar>

          <q-toolbar class="bg-transparent q-px-md">
            <q-input
              v-model="filterVals.search"
              :autofocus="$q.platform.is.desktop"
              placeholder="Find Files..."
              type="search"
              dense
              outlined
              clearable
              style="max-width: 225px;"
              :debounce="1000"
            >
              <template #prepend>
                <q-icon name="search" />
              </template>
            </q-input>
            <q-space />
            <q-item-label
              class="text-right text-caption"
              style="width: 100px;"
            >
              {{ commaNumber(total) }} {{ pluraliseString('result', total) }}
            </q-item-label>
          </q-toolbar>
        </q-header>

        <q-page-container>
          <q-page>
            <UploaderExtended
              v-if="!hideUploader"
              :allow-zip="!isSelectingImages"
              :hide-sublabel="isSelectingImages ? 'Supports JPG / PNG images.' : undefined"
              :hide-action-bar="hideUploaderActionBar"
              @uploaded="reloadBackToFirstPage"
              @start="startedWorking"
              @finish="finishedWorking"
            />

            <dynamic-table
              v-model:page="page"
              :headers="[]"
              :list="files"
              :showing-results="50"
              :total-results="total"
              :loading="loading"
              :group-func="item => filterVals.search ? true : $formatSystemDate(item.created_at, luxonFormats.FRIENDLY_DATE)"
              group-class="row q-col-gutter-md q-pa-md"
              responsive-width="none"
              pagination
              :anchor="null"
            >
              <template #group-name="{ group }">
                <sticky-header
                  color="black"
                  static
                >
                  {{ filterVals.search ? 'Search Results' : group }}
                </sticky-header>
              </template>

              <template #row="{ row }">
                <div class="col-6 col-sm-4 col-md-3 col-lg-2">
                  <SelectFileGridItem
                    :file="row"
                    :selected="isResultSelected(row.id)"
                    :limit-reached="hasReachedMaximum"
                    :is-single-select-mode="isSingleSelect"
                    :is-preview="previewMode"
                    @select="onClickResult(row)"
                    @preview="viewingFileID = row.id"
                  />
                </div>
              </template>

              <template #loading>
                <div class="row q-col-gutter-md q-pa-md">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="col-6 col-sm-4 col-md-3 col-lg-2"
                  >
                    <q-card>
                      <q-skeleton height="120px" square />

                      <q-card-section class="q-pa-sm">
                        <q-skeleton type="text" class="text-subtitle1" />
                        <q-skeleton type="text" class="text-caption" />
                      </q-card-section>
                    </q-card>
                  </div>
                </div>
              </template>

              <template #no-results>
                <no-results title="No files to display" without-icon />
              </template>
            </dynamic-table>
          </q-page>
        </q-page-container>

        <q-drawer
          v-model="filterDrawerOpen"
          :width="filterDrawerWidth"
          :breakpoint="filterBreakpoint"
          show-if-above
          side="right"
          class="bg-drawer"
        >
          <SelectFilesFilters
            :filter-vals="filterVals"
            :loading="uploading || loading"
            :restrict-to-type="restrictToType"
            :restrict-to-extension="restrictToExtension"
            @apply-filters="applyFilters"
          />
        </q-drawer>

        <q-footer
          v-if="maximum !== 1"
          class="blurred-container text-black"
        >
          <q-toolbar>
            <q-space />
            <q-btn
              :label="'Select ' + tempSelected.length + ' ' + pluraliseString('file', tempSelected.length)"
              color="green"
              icon-right="mdi-check"
              @click="saveSelectedResults"
            />
          </q-toolbar>
        </q-footer>
      </q-layout>
    </template>
  </content-dialog>

  <ViewFileDialog
    v-if="viewingFileID"
    :id="viewingFileID"
    :preview="previewMode"
    @close="viewingFileID = null"
    @saved="onSavedFile"
    @deleted="onDeleteFile"
  />
</template>

<script>
import SelectFileGridItem from './files/SelectGridItem.vue'
import ViewFileDialog from './view/Dialog'
import UploaderExtended from 'components/partials/uploader/UploaderExtended.vue'
import SelectFilesFilters from 'components/app/files/partials/SelectFilesFilters.vue'

export default {
  components: {
    UploaderExtended, SelectFileGridItem, ViewFileDialog, SelectFilesFilters
  },
  props: {
    // maximum = 0 : unlimited
    maximum: {
      type: Number,
      default: 1
    },
    // Title to show in the dialog header ("Select {title}s")
    filesTitle: {
      type: String,
      default: null
    },
    // Restrict results to specific file type. User cannot override this filter
    restrictToType: {
      type: String,
      default: null
    },
    // Restrict results to specific extension. User cannot override this filter
    restrictToExtension: {
      type: String,
      default: null
    },
    // Auto-hide results from a specific category name.
    // User can turn off this filter
    hideFromCatName: {
      type: String,
      default: null
    },
    // Auto-select a category when dialog is loaded.
    // User can remove filter which holds this category
    showFromCatName: {
      type: String,
      default: null
    },
    // Disable user being able to update/delete the drawing within File dialog button
    previewMode: {
      type: Boolean,
      default: true
    },
    // Hide file uploader from selection dialog
    hideUploader: {
      type: Boolean,
      default: false
    },
    // hide action bar with additional upload options
    hideUploaderActionBar: {
      type: Boolean,
      default: false
    },
    // Selected files
    selected: {
      type: [Array, Object],
      default: () => {
        return []
      }
    },
    // Append signed URL from backend payload
    appendSignedUrl: {
      type: Boolean,
      default: false
    }
  },
  emits: ['saved', 'deleted', 'fileUpdated'],
  data () {
    return {
      uploading: false,
      page: 1,
      total: 0,
      dialogOpen: false,
      loading: false,
      files: [],
      tempSelected: [],
      closeTimer: null,
      usageKey: null,
      viewingFileID: null,
      filterBreakpoint: 1024,
      filterDrawerWidth: 300,
      filterDrawerOpen: false,
      filterVals: {
        search: null,
        category: -1,
        hideFromCatName: null,
        type: null,
        creator: null
      }
    }
  },
  computed: {
    hasReachedMaximum () {
      return this.maximum > 1 && this.tempSelected.length === this.maximum
    },
    isSingleSelect () {
      return this.maximum === 1
    },
    mobileMode () {
      return this.windowWidth < this.filterBreakpoint
    },
    isSelectingImages () {
      return this.restrictToType === 'IMAGE'
    },
    dialogTitleStr () {
      const filesTitle = this.filesTitle || (this.isSelectingImages ? 'Image' : 'File')
      return `Select ${this.pluraliseString(filesTitle, this.maximum)}`
    }
  },
  watch: {
    'page' () {
      try {
        this.$refs.dialog.scrollTop()
      }
      catch (err) {}
      this.loadFiles()
    },
    'filterVals.search' () {
      this.loadToPageOne()
    }
  },
  methods: {
    loadToPageOne () {
      if (this.page !== 1) {
        this.page = 1
      }
      else {
        this.loadFiles()
      }
    },
    reloadBackToFirstPage (files) {
      // Try and select the files which were just uploaded
      if (files.length > 0) {
        files = files.filter(item => {
          const meetsExtension = !this.restrictToExtension || item.file_extension === this.restrictToExtension
          const meetsType = !this.restrictToType || item.type === this.restrictToType
          return meetsExtension && meetsType
        })
        if (this.maximum === 0 || (this.tempSelected.length + files.length) <= this.maximum) {
          for (const file of files) {
            this.onClickResult(file)
          }
        }
      }

      this.loadToPageOne()
    },
    open (key = null) {
      this.usageKey = key
      this.dialogOpen = true

      this.filterVals.type = this.restrictToType
      this.filterVals.hideFromCatName = this.hideFromCatName

      const performLoad = () => {
        let parseSelected = this.selected || []
        if (this.maximum === 1 && !!this.selected && !Array.isArray(this.selected)) {
          parseSelected = [this.selected]
        }
        this.tempSelected = JSON.parse(JSON.stringify(parseSelected))

        this.$nextTick(() => {
          this.loadFiles()
        })
      }

      // If a category name is specified as prop, attempt to fetch all available categories
      //  and grab the ID of the selected category
      if (this.showFromCatName) {
        this.$store.dispatch('filecategories/getFileCategories')
          .then(() => {
            const category = this.$store.getters['filecategories/getFileCategories']
              .find(item => item.name === this.showFromCatName)

            if (category) {
              this.filterVals.category = category.id
            }
          })
          .then(performLoad)
      }
      else {
        performLoad()
      }
    },
    async loadFiles (silent = false) {
      if (!silent) {
        this.loading = true
      }
      const { status, payload } = await this.$get('v1/files/search', {
        filter: this.filterVals.search,
        category: this.filterVals.category,
        hide_category: this.filterVals.hideFromCatName,
        type: this.filterVals.type,
        staff: this.filterVals.creator ? [this.filterVals.creator.id] : [],
        extension: this.restrictToExtension,
        page: this.page,
        rows: 50,
        sortBy: 'relevancy',
        sortDirection: 'desc',
        with_signed_url: this.appendSignedUrl ? 1 : 0
      })
      this.loading = false
      if (status === 'success') {
        this.total = payload.total
        this.files = payload.files
      }
    },
    onClickResult (file) {
      if (this.isResultSelected(file.id)) {
        this.removeResult(file.id)
      }
      else if (this.maximum === 1) {
        this.tempSelected = [file]

        clearTimeout(this.closeTimer)
        this.closeTimer = this.timed(() => {
          this.saveSelectedResults()
        }, 150)
      }
      else if (this.maximum === 0 || this.tempSelected.length < this.maximum) {
        this.tempSelected.push(file)
      }
    },
    findSelectedById (id) {
      return this.tempSelected.findIndex(item => item.id === id)
    },
    isResultSelected (id) {
      return this.findSelectedById(id) > -1
    },
    existsInOriginalSelected (id) {
      if (Array.isArray(this.selected)) {
        return this.selected.findIndex(item => item.id === id) > -1
      }
      else if (typeof this.selected === 'object' && !!this.selected) {
        return this.selected.id === id
      }
      return false
    },
    removeResult (id) {
      const index = this.findSelectedById(id)
      if (index > -1) {
        this.tempSelected.splice(index, 1)
      }
    },
    saveSelectedResults () {
      this.dialogOpen = false

      let selectedVal = this.tempSelected
      if (this.maximum === 1) {
        selectedVal = this.tempSelected.length > 0 ? this.tempSelected[0] : null
      }
      this.$emit('saved', selectedVal, this.usageKey)
    },
    startedWorking () {
      this.uploading = true
    },
    finishedWorking () {
      this.uploading = false
    },
    onSavedFile (file) {
      if (this.isResultSelected(file.id)) {
        this.removeResult(file.id)
        this.onClickResult(file)
      }

      // If file has been updated and exists in original selected prop,
      //  attempt to send data back to parent component to silently update if applicable
      if (this.existsInOriginalSelected(file.id)) {
        this.$emit('fileUpdated', file)
      }

      this.loadFiles(true)
    },
    onDeleteFile (id) {
      if (this.isResultSelected(id)) {
        this.removeResult(id)
      }

      // If file has been deleted and exists in original selected prop,
      //  attempt to send data back to parent component to silently remove if applicable
      if (this.existsInOriginalSelected(id)) {
        this.$emit('deleted', id)
      }

      this.loadFiles(true)
    },
    applyFilters () {
      this.loadToPageOne()
      if (this.mobileMode) {
        this.filterDrawerOpen = false
      }
    }
  }
}
</script>
